import React from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll, unlockBodyScroll } from '../../utils/modalUtils';
import './ConfirmationDialog.css';

const ConfirmationDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = 'Confirm', 
  cancelText = 'Cancel',
  confirmButtonClass = 'btn-danger',
  cancelButtonClass = 'btn-secondary',
  isLoading = false,
  loadingText = 'Processing...'
}) => {
  // Effect to manage body scroll locking
  React.useEffect(() => {
    if (isOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
    
    // Cleanup function to ensure we unlock scroll when component unmounts
    return () => {
      unlockBodyScroll();
    };
  }, [isOpen]);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Return null if modal is not open
  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  // Use createPortal to render the modal at the root level of the DOM
  return createPortal(
    <div className="confirmation-dialog-overlay" onClick={handleClose}>
      <div className="confirmation-dialog" onClick={e => e.stopPropagation()}>
        <div className="confirmation-dialog-header">
          <h3>{title}</h3>
          <button 
            className="close-button" 
            onClick={handleClose}
            disabled={isLoading}
          >
            ×
          </button>
        </div>
        
        <div className="confirmation-dialog-body">
          <div className="warning-icon">⚠️</div>
          <p>{message}</p>
        </div>
        
        <div className="confirmation-dialog-actions">
          <button
            type="button"
            className={`cancel-button ${cancelButtonClass}`}
            onClick={handleClose}
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={`confirm-button ${confirmButtonClass}`}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? loadingText : confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmationDialog; 